import {
  ActionIcon,
  AppShell,
  Burger,
  Group,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChartBar,
  IconLock,
  IconLogout,
  IconMoon,
  IconSun,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";

export function AppLayout() {
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  // Desktop starts expanded — collapsing is something a viewer opts into to
  // reclaim width, not the default state of a fresh session.
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
  const { me, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { setColorScheme } = useMantineColorScheme();
  const scheme = useComputedColorScheme("light", { getInitialValueInEffect: true });

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{
        width: 240,
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger
              opened={mobileOpened}
              onClick={toggleMobile}
              hiddenFrom="sm"
              size="sm"
            />
            <Burger
              opened={desktopOpened}
              onClick={toggleDesktop}
              visibleFrom="sm"
              size="sm"
            />
            <Text component={Link} to="/" fw={700} size="lg" td="none" c="inherit">
              JHU Summit Data Dashboard
            </Text>
          </Group>
          <Group gap="sm">
            <Text size="sm" c="dimmed" visibleFrom="xs">
              {me?.user.display_name || me?.user.email}
            </Text>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setColorScheme(scheme === "dark" ? "light" : "dark")}
              aria-label={`Switch to ${scheme === "dark" ? "light" : "dark"} theme`}
            >
              {scheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={4}>
            <NavLink
              component={Link}
              to="/"
              label="Projects"
              leftSection={<IconChartBar size={18} />}
              active={location.pathname === "/"}
            />
            <NavLink
              component={Link}
              to="/account"
              label="Account"
              leftSection={<IconUser size={18} />}
              active={location.pathname === "/account"}
            />
            {isAdmin && (
              <NavLink
                component={Link}
                to="/admin/users"
                label="Users"
                leftSection={<IconUsers size={18} />}
                active={location.pathname === "/admin/users"}
              />
            )}
            {isAdmin && (
              <NavLink
                component={Link}
                to="/admin/access"
                label="Project access"
                leftSection={<IconLock size={18} />}
                active={location.pathname === "/admin/access"}
              />
            )}
          </Stack>
        </AppShell.Section>

        <AppShell.Section>
          <UnstyledButton onClick={handleLogout} w="100%">
            <Group gap="xs" p="xs">
              <IconLogout size={18} />
              <Text size="sm">Sign out</Text>
            </Group>
          </UnstyledButton>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
